/* eslint-disable @typescript-eslint/no-explicit-any */
import { ExtendedColumnDef } from "components/Datatable";
import SortedCell from "./components/sorted-cell";
import { IAssignmentColumns } from "src/@types/analyst-assignment";
import React, { useMemo } from "react";
import {
  Controller,
  Control,
  FieldValues,
  UseFormSetValue,
  UseFormGetValues,
} from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { ISelectOptions } from "src/@types/select";

interface SelectCellProps {
  storeCode: string;
  defaultValue: any;
  options: ISelectOptions | undefined;
  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  setValidated: (bool: boolean) => void;
  fieldSuffix: string;
  placeholder: string;
}

const SelectCell = React.memo(function SelectCell({
  storeCode,
  defaultValue,
  options,
  control,
  setValue,
  getValues,
  setValidated,
  fieldSuffix,
  placeholder,
}: SelectCellProps) {
  const fieldName = `store_${storeCode}.${fieldSuffix}`;
  const isAnalyst = fieldSuffix === "analyst";

  return (
    <div className="table-form-scope w-full">
      <div className="flex align-center justify-center w-full checkbox select-table">
        <div className={`w-full flex items-center justify-center${isAnalyst ? " text-left" : ""}`}>
          <Controller
            name={fieldName}
            control={control}
            defaultValue={defaultValue}
            render={({ field }) => (
              <Select
                onValueChange={(value) => {
                  setValidated(false);
                  setValue(fieldName, value);
                }}
                value={field.value ?? defaultValue?.toString() ?? ""}
              >
                <SelectTrigger
                  className={`${isAnalyst ? " text-left " : ""}${
                    getValues(fieldName) ? "selected" : "not-selected"
                  }`}
                >
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent className={`border-2 border-[black]/20${isAnalyst ? " text-left" : ""}`}>
                  {options?.map((item) => (
                    <SelectItem
                      className={`cursor-pointer${isAnalyst ? " text-left" : ""}`}
                      key={item?.value ?? ""}
                      value={item?.value?.toString()}
                    >
                      {item?.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>
    </div>
  );
});

export const useColumns = (
  control: Control<FieldValues>,
  setValue: UseFormSetValue<FieldValues>,
  getValues: UseFormGetValues<FieldValues>,
  analystsOptions: ISelectOptions | undefined,
  regionalOptions: ISelectOptions | undefined,
  setValidated: (bool: boolean) => void
) => {
  const columns = useMemo<ExtendedColumnDef<IAssignmentColumns>[]>(
    () => [
      {
        accessorKey: "code",
        header: ({ column }) => (
          <SortedCell key={"code"} column={column} title={"Lojas"} />
        ),
        cell: ({ row }) => <div>{`Loja ${row?.original?.code}`}</div>,
        className: "store-column",
      },
      {
        accessorKey: "responsible",
        header: ({ column }) => (
          <SortedCell
            column={column}
            title={"Responsável"}
            key={"responsible.name"}
          />
        ),
        cell: ({ row }) => (
          <SelectCell
            storeCode={row.original.code}
            defaultValue={row.original.responsible?.id}
            options={analystsOptions}
            control={control}
            setValue={setValue}
            getValues={getValues}
            setValidated={setValidated}
            fieldSuffix="analyst"
            placeholder="Selecione um responsável"
          />
        ),
        className: "responsible-column",
      },
      {
        accessorKey: "regional",
        header: "Regional",
        className: "regional-responsible-column",
        cell: ({ row }) => (
          <SelectCell
            storeCode={row.original.code}
            defaultValue={row.original.regional?.id?.toString()}
            options={regionalOptions}
            control={control}
            setValue={setValue}
            getValues={getValues}
            setValidated={setValidated}
            fieldSuffix="regional"
            placeholder="Selecione um responsável regional"
          />
        ),
      },
    ],
    [analystsOptions, regionalOptions, control, setValue, getValues, setValidated]
  );

  return columns;
};
